"use client";

/**
 * Keeps a client-view reader out of the areas their sidebar does not offer.
 *
 * WHY THIS EXISTS. `productAreas.ts` gates the NAVIGATION on `visibility`, and says so
 * explicitly: "This gates the NAV ONLY." That was the right scope when it was written —
 * artefact entitlement is enforced server-side, so a planner following a deep link into the
 * study gets exactly what they were always entitled to and nothing more.
 *
 * It stopped being sufficient the moment the GLA session was planned around VIEW-AS. The client
 * is to be shown the product in GLA mode "so hidden elements stay hidden" (Doug, call notes
 * 2026-09-24), and hiding a link does not hide a route: a reader already on `/report` when the
 * view switches, a back-button, a bookmark or a stale tab all land on the full study — with its
 * table references, its configuration disclosure and its published-report comparisons — inside
 * a session whose entire point is that those are not on screen. Found exactly that way
 * (Neil, 2026-09-24), which is the only reason this is a guard rather than a note.
 *
 * THIS IS PRESENTATION, NOT SECURITY, and the distinction has to survive this file. Nothing here
 * protects data: a super_admin viewing as a tenant is still a super_admin to the API and still
 * entitled to everything they were before. What this changes is where the PRODUCT will take
 * them while they are deliberately looking through a client's eyes. Anything that would leak if
 * this hook were deleted is in the wrong place — server-side gating is `ArtefactSpec.roles`,
 * `require_role` and RLS, all unchanged.
 *
 * DERIVED FROM THE SAME REGISTRY AS THE SIDEBAR, so the two cannot drift. An area hidden from
 * the nav is an area this redirects away from, by construction — flipping one word in
 * `productAreas.ts` moves both together, and nobody has to remember there are two lists.
 *
 * NOBODY GETS STUCK. The exit from a client view is `ViewingAsBanner`, mounted unconditionally
 * in `AppShell` with its own "stop viewing" control, so it is on screen on every page including
 * the one this redirects to. It was never the Admin nav that got a reader out.
 */

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";

import { PRODUCT_AREAS } from "@/features/navigation/productAreas";

import { useClientView } from "./useClientView";

/** Where a client-view reader is sent. The product's front door for that role. */
const CLIENT_HOME = "/household";

/**
 * Routes that sit inside a super_admin area in the registry but are NOT part of it, and must
 * stay reachable by anyone.
 *
 * `/about` and `/data-access` are listed under the Admin area purely because that is where the
 * links were put; they are ordinary informational pages, and a planner who opens "About" should
 * get About rather than a redirect to a dashboard. Listing them here rather than restructuring
 * the registry keeps this change to the behaviour that was reported, on the evening before a
 * client session — the registry tidy-up is worth doing, but not now and not silently.
 */
const ALWAYS_ALLOWED = ["/about", "/data-access"];

/** Every path prefix owned by an area the given role cannot see. */
export function blockedPrefixesFor(role: string | undefined): string[] {
  if (role === "super_admin") return [];
  return (
    PRODUCT_AREAS.filter((area) => area.visibility === "super_admin")
      .flatMap((area) => area.items.map((item) => item.href))
      // An href may carry a hash (Report's subsection links); the ROUTE is what is guarded.
      .map((href) => href.split("#")[0]!)
      .filter((href) => !ALWAYS_ALLOWED.includes(href))
  );
}

/** True when `pathname` is inside one of the blocked areas. */
export function isBlocked(pathname: string, role: string | undefined): boolean {
  if (ALWAYS_ALLOWED.includes(pathname)) return false;
  return blockedPrefixesFor(role).some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

export function useClientViewRouteGuard(): void {
  const router = useRouter();
  const pathname = usePathname();
  const { effectiveRole } = useClientView();

  useEffect(() => {
    if (!pathname) return;
    if (!isBlocked(pathname, effectiveRole)) return;
    /* `replace`, not `push`: the blocked page must not sit in history, or the reader's next
       Back lands straight back on it — which for a session being watched by the client is the
       same leak arriving a second time. */
    router.replace(CLIENT_HOME);
  }, [router, pathname, effectiveRole]);
}
